import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { PublicClassNames } from 'embed/constants'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { SortDirection } from 'even-ts-static'
const defaultStyles = require('./styles.scss')
const SortDirectionIcon = require('./sort-direction.svg')

export interface SortOptionsProps {
  showDefaultSortOption?: boolean
  sortOptionValues: object
  sort?: string
  sortDirection: SortDirection
  onSortOptionClick: (sortOption: string) => void
  customStyles?: SortStyles
  itemWidth?: boolean
}

interface SortOptionProps {
  className?: string
  customStyles?: SortStyles
  itemWidth?: boolean
  onSortOptionClick: (sortOption: string) => void
  option?: string
  sort?: string
  children: React.ReactNode
}

export interface SortStyles {
  sort: string
  sortItem: string
  sortItemActive: string
  sortItemLabel: string
  sortDirection: string
}

const DEFAULT_SORT_OPTION = 'Recommended'

const SortOption: React.SFC<SortOptionProps> = ({
  className,
  customStyles,
  itemWidth,
  onSortOptionClick,
  option,
  sort,
  children,
}) => (
  <ThemeConsumer>
    {({ borderRadiusSmall, colorSecondary, fontFamilySecondary }) => (
      <li
        className={classnames(
          defaultStyles.sortItem,
          { [defaultStyles.sortItemActive]: option === sort },
          customStyles?.sortItem,
          customStyles && {
            [customStyles.sortItemActive]: option === sort,
          },
          { [defaultStyles.sortItemCredit]: itemWidth },
          className,
        )}
        key={option}
        onClick={onSortOptionClick.bind(null, option)}
      >
        <span
          className={classnames(defaultStyles.sortItemLabel, customStyles?.sortItemLabel, {
            [PublicClassNames.SortControlsActive]: option === sort,
            [css(`
          background: ${colorSecondary};
          &:hover {
            background: ${colorSecondary};
            opacity: 0.84;
          }
          border-radius: ${borderRadiusSmall};
          `)]: option === sort,
          })}
        >
          {children}
        </span>
      </li>
    )}
  </ThemeConsumer>
)

const SortOptions: React.SFC<SortOptionsProps> = ({
  showDefaultSortOption,
  sortOptionValues,
  sort,
  sortDirection,
  onSortOptionClick,
  customStyles,
  itemWidth,
}) => (
  <ThemeConsumer>
    {({ borderRadiusSmall, colorBorder, colorTextPrimary, labelStyles }) => (
      <div className={classnames(PublicClassNames.SortControls, defaultStyles.resultsSort)}>
        <span className={defaultStyles.resultsLabel}>
          <span className={classnames(defaultStyles.sortResultsLabel)} style={labelStyles}>
            Sort<span> By</span>
          </span>
          <span
            className={classnames(
              defaultStyles.sortResultsButton,
              css(`border-color: ${colorBorder} !important; border-radius: ${borderRadiusSmall}`),
            )}
          >
            {sort !== undefined ? (
              <React.Fragment>
                {sortOptionValues[sort]}
                <SortDirectionIcon
                  className={classnames(
                    defaultStyles.sortDirection,
                    defaultStyles[`sortDirection-${sortDirection}`],
                    css(`path { fill: ${colorTextPrimary} }`),
                  )}
                />
              </React.Fragment>
            ) : (
              DEFAULT_SORT_OPTION
            )}
            <span className={defaultStyles.resultsLabelCarat} />
          </span>
        </span>
        <ul className={classnames(defaultStyles.sort, customStyles?.sort)}>
          {Object.keys(sortOptionValues).map((option) => (
            <SortOption
              customStyles={customStyles}
              key={option}
              option={option}
              sort={sort}
              itemWidth={itemWidth}
              onSortOptionClick={onSortOptionClick}
            >
              {sortOptionValues[option]}
              {option === sort && (
                <SortDirectionIcon
                  className={classnames(
                    defaultStyles.sortDirection,
                    defaultStyles[`sortDirection-${sortDirection}`],
                    css(`path { fill: ${option === sort ? '#fff' : colorTextPrimary} }`),
                  )}
                />
              )}
            </SortOption>
          ))}
          {showDefaultSortOption === true && (
            <SortOption
              className={defaultStyles.defaultSort}
              customStyles={customStyles}
              option={undefined}
              sort={sort}
              itemWidth={itemWidth}
              onSortOptionClick={onSortOptionClick}
            >
              {DEFAULT_SORT_OPTION}
            </SortOption>
          )}
        </ul>
      </div>
    )}
  </ThemeConsumer>
)

SortOptions.defaultProps = {
  customStyles: {
    sort: '',
    sortDirection: '',
    sortItem: '',
    sortItemActive: '',
    sortItemLabel: '',
  },
}

export default SortOptions
